import React, { useEffect, useRef, useState } from "react";
import {
  LuClock,
  LuEye,
  LuFileText,
  LuLifeBuoy,
  LuLoader2,
  LuLock,
  LuSearch,
  LuShieldCheck,
} from "react-icons/lu";
import {
  beginImpersonation,
  getImpersonation,
  isSupportStaff,
} from "../../services/auth/impersonation-service";
import { AccountOption, ErrorAlert, Guarantee, StepHeader, card, field, primaryButton } from "./components";
import {
  createImpersonationSession,
  endImpersonation,
  searchAccounts,
} from "../../services/support/support-service";

const SEARCH_FIELDS = [
  { value: 'email', label: 'Email', placeholder: 'owner@customer.com', type: 'email' },
  { value: 'accountId', label: 'Account ID', placeholder: 'Paste an account ID', type: 'text' },
  { value: 'appId', label: 'App ID', placeholder: 'Paste an app ID', type: 'text' },
];
const MIN_REASON_LENGTH = 5;

/** Support staff (bc-support): find a customer account and open a read-only impersonation session. */
export default function SupportPage() {
  const [staff, setStaff] = useState(null);
  const [searchBy, setSearchBy] = useState('email');
  const [query, setQuery] = useState('');
  const [accounts, setAccounts] = useState(null);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState('');
  const [ticketRef, setTicketRef] = useState('');
  const [searching, setSearching] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const reasonRef = useRef(null);
  const active = getImpersonation();
  const searchField = SEARCH_FIELDS.find(f => f.value === searchBy);
  const reasonOk = reason.trim().length >= MIN_REASON_LENGTH;

  useEffect(() => {
    isSupportStaff().then(setStaff);
  }, []);

  useEffect(() => {
    if (selected) reasonRef.current?.focus();
  }, [selected]);

  const handleSearch = async e => {
    e.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setError(null);
    setSelected(null);
    const { response, status } = await searchAccounts({ [searchBy]: query.trim() });
    if (status === 200) {
      const found = response.accounts || [];
      setAccounts(found);
      if (found.length === 1) setSelected(found[0]);
    } else {
      setAccounts(null);
      setError(response || 'Search failed');
    }
    setSearching(false);
  };

  const handleStart = async e => {
    e.preventDefault();
    if (!selected || !reasonOk) return;
    setStarting(true);
    setError(null);
    const { response, status } = await createImpersonationSession({
      targetAccountId: selected.accountId,
      reason: reason.trim(),
      ticketRef: ticketRef.trim() || undefined,
    });
    if (status === 200 || status === 201) {
      beginImpersonation(response);
      return;
    }
    setError(response || 'Could not start the session');
    setStarting(false);
  };

  if (staff === null) {
    return (
      <div role="status" className="flex items-center justify-center gap-2 py-24 text-sm text-gray-500">
        <LuLoader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading…
      </div>
    );
  }

  if (!staff) {
    return (
      <div className="mx-auto max-w-md px-6 py-24 text-center">
        <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-100">
          <LuLock className="h-5 w-5 text-gray-500" aria-hidden="true" />
        </span>
        <h1 className="text-lg font-semibold text-gray-900">Support access only</h1>
        <p className="mt-1 text-sm text-gray-500">
          This page is only available to BotCircuits support staff.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10">
      {/* Header */}
      <div className="mb-8 flex items-start gap-4">
        <span className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-gray-900 text-brand-lime shadow-sm">
          <LuLifeBuoy className="h-6 w-6" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Customer support</h1>
          <p className="mt-1 text-sm text-gray-500">
            View a customer's account exactly as its owner sees it, to investigate their agents, workflows and conversations.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Guarantee icon={LuEye}>Read-only</Guarantee>
            <Guarantee icon={LuClock}>60-minute sessions</Guarantee>
            <Guarantee icon={LuFileText}>Recorded in the account's audit log</Guarantee>
          </div>
        </div>
      </div>

      {error && <div className="mb-4"><ErrorAlert message={error} onDismiss={() => setError(null)} /></div>}

      {active ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5 dark:border-red-900/60 dark:bg-red-950/30 sm:p-6">
          <div className="flex flex-wrap items-center gap-4">
            <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-red-600 text-fixed-white">
              <LuEye className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-red-900 dark:text-red-200">
                You're viewing {active.targetAccountName || active.targetAccountId}
              </p>
              <p className="text-sm text-red-700 dark:text-red-300">End that session before starting another.</p>
            </div>
            <button type="button" onClick={() => endImpersonation()}
              className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-medium text-fixed-white shadow-sm transition hover:bg-red-700 focus:outline-none focus:ring-4 focus:ring-red-600/20">
              End session
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Step 1: find the account */}
          <section className={card} aria-labelledby="support-find">
            <StepHeader step={1} title={<span id="support-find">Find the customer account</span>} description="Search by the email of any user in the account, or by an ID from a ticket." />

            <form onSubmit={handleSearch} className="space-y-3">
              <div role="radiogroup" aria-label="Search by" className="inline-flex rounded-xl bg-gray-100 p-1">
                {SEARCH_FIELDS.map(f => (
                  <button
                    key={f.value}
                    type="button"
                    role="radio"
                    aria-checked={searchBy === f.value}
                    onClick={() => setSearchBy(f.value)}
                    className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${searchBy === f.value
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-900'}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              <div className="flex flex-col gap-2 sm:flex-row">
                <div className="relative flex-1">
                  <LuSearch className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                  <input
                    aria-label="Search value"
                    type={searchField.type}
                    className={`${field} pl-10`}
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder={searchField.placeholder}
                    autoComplete="off"
                    autoFocus
                  />
                </div>
                <button type="submit" className={primaryButton} disabled={searching || !query.trim()}>
                  {searching
                    ? <LuLoader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    : <LuSearch className="h-4 w-4" aria-hidden="true" />}
                  Search
                </button>
              </div>
            </form>

            {accounts && accounts.length === 0 && (
              <div className="mt-5 rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center">
                <p className="text-sm font-medium text-gray-900">No matching account</p>
                <p className="mt-1 text-sm text-gray-500">Check the value, or try searching by another field.</p>
              </div>
            )}

            {accounts && accounts.length > 0 && (
              <div className="mt-5">
                <p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-400">
                  {accounts.length} {accounts.length === 1 ? 'account' : 'accounts'} found
                </p>
                <div role="radiogroup" aria-label="Customer account" className="space-y-2">
                  {accounts.map(account => (
                    <AccountOption
                      key={account.accountId}
                      account={account}
                      selected={selected?.accountId === account.accountId}
                      onSelect={setSelected}
                    />
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Step 2: open the session */}
          {selected && (
            <section className={card} aria-labelledby="support-start">
              <StepHeader step={2} title={<span id="support-start">Open a read-only session</span>} description={`For ${selected.name || selected.accountId}. The reason is saved in the account's audit log.`} />

              <form onSubmit={handleStart} className="space-y-4">
                <div>
                  <label htmlFor="support-reason" className="mb-1.5 block text-sm font-medium text-gray-700">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="support-reason"
                    ref={reasonRef}
                    className={`${field} resize-none`}
                    rows={3}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="e.g. Customer reports the booking workflow stops after the date question"
                  />
                  <p className={`mt-1 text-xs ${reasonOk || !reason ? 'text-gray-400' : 'text-amber-600'}`}>
                    At least {MIN_REASON_LENGTH} characters.
                  </p>
                </div>

                <div>
                  <label htmlFor="support-ticket" className="mb-1.5 block text-sm font-medium text-gray-700">
                    Ticket reference <span className="font-normal text-gray-400">(optional)</span>
                  </label>
                  <input
                    id="support-ticket"
                    className={field}
                    value={ticketRef}
                    onChange={e => setTicketRef(e.target.value)}
                    placeholder="SUP-1234"
                    autoComplete="off"
                  />
                </div>

                <div className="rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-600">
                  <p className="flex items-center gap-2 font-medium text-gray-900">
                    <LuShieldCheck className="h-4 w-4" aria-hidden="true" /> What you can do
                  </p>
                  <ul className="mt-1.5 space-y-1 pl-6 text-xs leading-relaxed">
                    <li className="list-disc">Browse apps, workflows, tools, knowledge, conversations, traces and analytics.</li>
                    <li className="list-disc">No changes, publishing, exports or simulator chats; access keys and payment details stay hidden.</li>
                    <li className="list-disc">Only this browser tab switches to the customer's account.</li>
                  </ul>
                </div>

                <div className="flex justify-end">
                  <button type="submit" className={primaryButton} disabled={starting || !reasonOk}>
                    {starting
                      ? <LuLoader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      : <LuEye className="h-4 w-4" aria-hidden="true" />}
                    Start read-only session
                  </button>
                </div>
              </form>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
